import { useEffect, useRef, useState } from 'react'
import { pref, setPref } from '@/components/camera/recordStore'
import { countWords, splitParas } from '@/components/teleprompter/prompter'

/**
 * Practice (was Read only): the teleprompter without the camera, for reading
 * through or for filming on another camera. Brian's after/record-read.html
 * v1.1 (Oct 7 2026), opened from Record's modes.
 *
 * Full screen, the script only, 30px on a phone (38 wider), at most 720px
 * wide, scrolling up past a dashed reading line 38% down. It starts moving at
 * once. ⏪ 5s · ⏸ Pause · 5s ⏩ · ✕, then − WPM + (70 to 300, shared with
 * Record), A− / A+ (20 to 64px, remembered) and Mirror (for a beam-splitter
 * prompter). Tap the text to pause or go on. Keys: Space, ↑↓ ±10 WPM, ←→ 5 s,
 * Esc. The speed pedals: hold Fast to ramp up to 2x, Slow to ease down to a
 * quarter; let go and it coasts back. The set WPM never changes.
 *
 * Mirror has its own key here (gogrov.readmirror): Record's Mirror (on by
 * default) means the camera picture, and sharing Brian's one key would open
 * Practice with the text backwards.
 */
const MAX = 2, MIN = 0.25, PUSH = 0.45, COAST = 0.7

export function Practice({ script, onClose }: { script: string; onClose: () => void }) {
  const viewRef = useRef<HTMLDivElement>(null)
  const txtRef = useRef<HTMLDivElement>(null)
  const progRef = useRef<HTMLDivElement>(null)
  const pedLRef = useRef<HTMLDivElement>(null)
  const pedRRef = useRef<HTMLDivElement>(null)
  const paras = splitParas(script)
  const words = countWords(script)

  const [wpm, setWpmState] = useState(() => Math.max(70, Math.min(300, +pref('gogrov.wpm', '130') || 130)))
  const [size, setSizeState] = useState(() => +pref('gogrov.readsize', String(window.innerWidth > 600 ? 38 : 30)) || 30)
  const [mirror, setMirrorState] = useState(() => pref('gogrov.readmirror', '0') === '1')
  const [paused, setPaused] = useState(false)
  const [m, setM] = useState(1)

  const st = useRef({ offset: 0, paused: false, m: 1, target: 1, last: 0, wpm, words })
  st.current.wpm = wpm; st.current.words = words; st.current.paused = paused

  const setWpm = (v: number) => { const w = Math.max(70, Math.min(300, v)); setWpmState(w); setPref('gogrov.wpm', String(w)) }
  const setSize = (v: number) => { const z = Math.max(20, Math.min(64, v)); setSizeState(z); setPref('gogrov.readsize', String(z)) }
  const setMirror = (v: boolean) => { setMirrorState(v); setPref('gogrov.readmirror', v ? '1' : '0') }

  const geom = () => {
    const vh = viewRef.current?.clientHeight ?? 0, total = txtRef.current?.scrollHeight ?? 0
    const s = st.current, secs = (s.words / s.wpm) * 60
    return { startY: vh * 0.38, lead: vh * 0.22, total, pps: total / Math.max(1, secs) }
  }
  const place = () => {
    const g = geom(), s = st.current
    if (txtRef.current) txtRef.current.style.transform = `translate(-50%, ${g.startY + g.lead - s.offset}px)`
    if (progRef.current) progRef.current.style.width = `${Math.min(100, (s.offset / Math.max(1, g.total + g.lead)) * 100)}%`
  }

  useEffect(() => {
    let raf = 0
    const tick = (now: number) => {
      const s = st.current
      const dt = Math.min(0.1, (now - (s.last || now)) / 1000); s.last = now
      const was = s.m
      s.m += (s.target - s.m) * (1 - Math.exp(-dt / (s.target === 1 ? COAST : PUSH)))
      if (Math.abs(s.m - 1) < 0.005 && s.target === 1) s.m = 1
      pedRRef.current?.style.setProperty('--g', Math.max(0, (s.m - 1) / (MAX - 1)).toFixed(3))
      pedLRef.current?.style.setProperty('--g', Math.max(0, (1 - s.m) / (1 - MIN)).toFixed(3))
      if (was !== s.m) setM(s.m)
      const g = geom()
      if (!s.paused) s.offset = Math.min(s.offset + dt * g.pps * s.m, g.total + g.lead)
      place()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = () => setPaused((p) => !p)
  const jump = (sec: number) => { const s = st.current; s.offset = Math.max(0, s.offset + sec * geom().pps); place() }

  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined)
  keyRef.current = (e: KeyboardEvent) => {
    if (e.key === ' ') { e.preventDefault(); toggle() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setWpm(wpm + 10) }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setWpm(wpm - 10) }
    else if (e.key === 'ArrowLeft') jump(-5)
    else if (e.key === 'ArrowRight') jump(5)
    else if (e.key === 'Escape') onClose()
  }
  useEffect(() => {
    // capture: Record's own keys underneath wait while Practice is open
    const k = (e: KeyboardEvent) => { e.stopImmediatePropagation(); keyRef.current(e) }
    addEventListener('keydown', k, true)
    return () => removeEventListener('keydown', k, true)
  }, [])

  const pad = (to: number) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault(); e.stopPropagation()
      st.current.target = to
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* */ }
      if (st.current.paused) setPaused(false)
    },
    onPointerUp: () => { if (st.current.target === to) st.current.target = 1 },
    onPointerCancel: () => { if (st.current.target === to) st.current.target = 1 },
    onLostPointerCapture: () => { if (st.current.target === to) st.current.target = 1 },
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  })

  const live = Math.abs(m - 1) > 0.03
  return (
    <div className="rec-read" role="dialog" aria-modal="true" aria-label="Practice">
      <div className="rec-read__view" ref={viewRef} onClick={toggle}>
        <div className={`rec-read__txt${mirror ? ' mirror' : ''}`} ref={txtRef} style={{ fontSize: size }}>
          {paras.length ? paras.map((p, i) => <p key={i}>{p}</p>) : <p>No script for this story yet.</p>}
        </div>
        <div className="rec-read__mark" />
        <div className={`rec-read__paused${paused ? ' on' : ''}`}>Paused · tap to go on</div>
      </div>
      <div className="rec-read__prog" ref={progRef} />
      <div className="rec-read__bar">
        <div className="row">
          <button type="button" onClick={() => jump(-5)}>⏪ 5s</button>
          <button type="button" className="big" onClick={toggle}>{paused ? '▶ Go on' : '⏸ Pause'}</button>
          <button type="button" onClick={() => jump(5)}>5s ⏩</button>
          <button type="button" className="x" aria-label="Exit" onClick={onClose}>✕</button>
        </div>
        <div className="row">
          <button type="button" aria-label="Slower" onClick={() => setWpm(wpm - 10)}>−</button>
          <span className={`val${live ? ' live' : ''}`}>{live ? `${Math.round(wpm * m)} WPM ${m > 1 ? '↑' : '↓'}` : `${wpm} WPM`}</span>
          <button type="button" aria-label="Faster" onClick={() => setWpm(wpm + 10)}>+</button>
          <button type="button" aria-label="Smaller text" onClick={() => setSize(size - 2)}>A−</button>
          <button type="button" aria-label="Bigger text" onClick={() => setSize(size + 2)}>A+</button>
          <button type="button" aria-pressed={mirror} onClick={() => setMirror(!mirror)}>Mirror</button>
        </div>
      </div>
      <div className="rec-read__dock"><b className={live ? 'live' : ''}>{Math.round(wpm * m)}</b><small>WPM · hold a pad</small></div>
      <div className="rec-read__pedal l" ref={pedLRef} role="button" aria-label="Hold to slow down" {...pad(MIN)}>
        <span className="tri" /><small>Slow</small>
        <svg viewBox="0 0 24 24"><path d="M12 18l-6-6 6-6M19 18l-6-6 6-6" /></svg>
      </div>
      <div className="rec-read__pedal r" ref={pedRRef} role="button" aria-label="Hold to speed up" {...pad(MAX)}>
        <span className="tri" /><small>Fast</small>
        <svg viewBox="0 0 24 24"><path d="M5 6l6 6-6 6M12 6l6 6-6 6" /></svg>
      </div>
    </div>
  )
}
